import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Building2,
  Users,
  DollarSign,
  TrendingUp,
  Plus,
  Edit2,
  Trash2,
  Search,
  RefreshCw,
  AlertCircle,
  XCircle,
  CheckCircle,
  Phone,
  Mail,
  MapPin,
  BarChart3
} from 'lucide-react';
import './DepartmentTab.css';

const RAW_API_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const API_BASE_URL = RAW_API_URL.replace(/\/+$/, '').endsWith('/api')
  ? RAW_API_URL.replace(/\/+$/, '')
  : `${RAW_API_URL.replace(/\/+$/, '')}/api`;

const buildDepartmentCode = (name = '') => {
  const normalized = String(name).trim();
  if (!normalized) return '';

  const words = normalized.split(/\s+/).filter(Boolean);
  const acronym = words.map((word) => word[0]).join('').toUpperCase();
  if (acronym.length >= 2) return acronym.slice(0, 6);

  return normalized.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 6);
};

const getApiErrorMessage = (err, fallbackMessage) => {
  const apiMessage = err?.response?.data?.message || err?.response?.data?.error;
  if (typeof apiMessage === 'string' && apiMessage.trim()) {
    return apiMessage;
  }
  return fallbackMessage;
};

const DepartmentTab = ({ hotelId, token }) => {
  // Data state
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [departmentStats, setDepartmentStats] = useState(null);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeView, setActiveView] = useState('grid'); // grid or details
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    manager_id: '',
    budget: '',
    contact_email: '',
    contact_phone: '',
    location: ''
  });

  // Fetch departments on mount
  useEffect(() => {
    if (hotelId && token) {
      fetchDepartments();
    }
  }, [hotelId, token]);

  // API Calls
  const fetchDepartments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/departments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('[DepartmentTab] Fetch response:', response.data);
      const rows = response.data?.data || response.data || [];
      setDepartments(rows.map((dept) => ({
        id: dept.department_id ?? dept.id,
        name: dept.department_name ?? dept.name,
        description: dept.description,
        manager_name: dept.manager_name,
        manager_id: dept.head_of_department ?? dept.manager_id,
        budget: dept.budget,
        staff_count: Number(dept.staff_count || 0),
        contact_email: dept.email ?? dept.contact_email,
        contact_phone: dept.phone ?? dept.contact_phone,
        location: dept.location
      })));
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load departments'));
      console.error('[DepartmentTab] Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartmentDetails = async (departmentId) => {
    setLoading(true);
    setError(null);
    try {
      const [detailsResponse, statsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/departments/${departmentId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/departments/${departmentId}/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setSelectedDepartment(detailsResponse.data.data || detailsResponse.data);
      setDepartmentStats(statsResponse.data.data || statsResponse.data);
      setActiveView('details');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load department details'));
      console.error('[DepartmentTab] Details error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDepartment = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await axios.post(
        `${API_BASE_URL}/departments`,
        {
          department_name: formData.name,
          department_code: buildDepartmentCode(formData.name),
          description: formData.description,
          manager_id: formData.manager_id || null,
          email: formData.contact_email || null,
          phone: formData.contact_phone || null,
          hotel_id: hotelId,
          budget: formData.budget ? parseFloat(formData.budget) : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setShowModal(false);
      resetForm();
      fetchDepartments();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to create department'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDepartment = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await axios.put(
        `${API_BASE_URL}/departments/${editingDepartment.id}`,
        {
          department_name: formData.name,
          description: formData.description,
          manager_id: formData.manager_id || null,
          email: formData.contact_email || null,
          phone: formData.contact_phone || null,
          budget: formData.budget ? parseFloat(formData.budget) : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setShowModal(false);
      setEditingDepartment(null);
      resetForm();
      fetchDepartments();
      
      // Update selected department if viewing details
      if (selectedDepartment?.id === editingDepartment.id) {
        fetchDepartmentDetails(editingDepartment.id);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to update department'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDepartment = async (departmentId) => {
    if (!window.confirm('Are you sure you want to delete this department? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await axios.delete(`${API_BASE_URL}/departments/${departmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      fetchDepartments();
      
      // Close details view if deleted department was selected
      if (selectedDepartment?.id === departmentId) {
        setSelectedDepartment(null);
        setDepartmentStats(null);
        setActiveView('grid');
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to delete department. Make sure there are no staff members assigned.'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      manager_id: '',
      budget: '',
      contact_email: '',
      contact_phone: '',
      location: ''
    });
  };

  const openCreateModal = () => {
    setEditingDepartment(null);
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (department) => {
    setEditingDepartment(department);
    setFormData({
      name: department.name || '',
      description: department.description || '',
      manager_id: department.manager_id || '',
      budget: department.budget || '',
      contact_email: department.contact_email || '',
      contact_phone: department.contact_phone || '',
      location: department.location || ''
    });
    setShowModal(true);
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const calculateBudgetUtilization = () => {
    if (!selectedDepartment?.budget || !departmentStats?.total_salaries) return 0;
    return (departmentStats.total_salaries / selectedDepartment.budget) * 100;
  };

  const filteredDepartments = departments.filter(dept =>
    dept.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    dept.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="department-tab">
      {/* Header */}
      <div className="department-header">
        <div className="header-left">
          <h2>
            <Building2 size={24} />
            Department Management
          </h2>
          <p>Manage departments, budgets, and organizational structure</p>
        </div>
        <div className="header-actions">
          {activeView === 'details' && (
            <button className="btn-secondary" onClick={() => {
              setActiveView('grid');
              setSelectedDepartment(null);
              setDepartmentStats(null);
            }}>
              ← Back to Departments
            </button>
          )}
          <button className="btn-icon" onClick={fetchDepartments}>
            <RefreshCw size={18} />
          </button>
          {activeView === 'grid' && (
            <button className="btn-primary" onClick={openCreateModal}>
              <Plus size={18} />
              Add Department
            </button>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-banner">
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={() => setError(null)}><XCircle size={16} /></button>
        </div>
      )}

      {/* Grid View */}
      {activeView === 'grid' && (
        <div className="departments-view">
          {/* Search Bar */}
          <div className="search-bar">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search departments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Summary Cards */}
          <div className="summary-cards">
            <div className="summary-card">
              <div className="card-icon departments">
                <Building2 size={24} />
              </div>
              <div className="card-content">
                <h3>{departments.length}</h3>
                <p>Total Departments</p>
              </div>
            </div>
            <div className="summary-card">
              <div className="card-icon staff">
                <Users size={24} />
              </div>
              <div className="card-content">
                <h3>{departments.reduce((sum, d) => sum + (d.staff_count || 0), 0)}</h3>
                <p>Total Staff</p>
              </div>
            </div>
            <div className="summary-card">
              <div className="card-icon budget">
                <DollarSign size={24} />
              </div>
              <div className="card-content">
                <h3>{formatCurrency(departments.reduce((sum, d) => sum + (parseFloat(d.budget) || 0), 0))}</h3>
                <p>Total Budget</p>
              </div>
            </div>
          </div>

          {/* Departments Grid */}
          {loading ? (
            <div className="loading-state">
              <RefreshCw className="spin" size={32} />
              <p>Loading departments...</p>
            </div>
          ) : (
            <div className="departments-grid">
              {filteredDepartments.map(department => (
                <div 
                  key={department.id} 
                  className="department-card"
                  onClick={() => fetchDepartmentDetails(department.id)}
                >
                  <div className="card-header">
                    <div className="department-icon">
                      <Building2 size={28} />
                    </div>
                    <div className="card-actions" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => openEditModal(department)} title="Edit">
                        <Edit2 size={16} />
                      </button>
                      <button onClick={() => handleDeleteDepartment(department.id)} title="Delete">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  
                  <h3>{department.name}</h3>
                  {department.description && (
                    <p className="department-description">{department.description}</p>
                  )}
                  
                  <div className="department-stats">
                    <div className="stat-item">
                      <Users size={16} />
                      <span>{department.staff_count || 0} Staff</span>
                    </div>
                    {department.budget && (
                      <div className="stat-item">
                        <DollarSign size={16} />
                        <span>{formatCurrency(department.budget)}</span>
                      </div>
                    )}
                  </div>

                  {department.manager_name && (
                    <div className="department-manager">
                      <span className="manager-label">Manager:</span>
                      <span className="manager-name">{department.manager_name}</span>
                    </div>
                  )}
                </div>
              ))}
              
              {filteredDepartments.length === 0 && (
                <div className="empty-state">
                  <Building2 size={48} />
                  <h3>No departments found</h3>
                  <p>Create your first department to get started</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Details View */}
      {activeView === 'details' && selectedDepartment && (
        <div className="department-details">
          {loading ? (
            <div className="loading-state">
              <RefreshCw className="spin" size={32} />
              <p>Loading details...</p>
            </div>
          ) : (
            <>
              {/* Department Header */}
              <div className="details-header">
                <div className="header-info">
                  <div className="department-icon-large">
                    <Building2 size={40} />
                  </div>
                  <div>
                    <h2>{selectedDepartment.name}</h2>
                    {selectedDepartment.description && (
                      <p className="details-description">{selectedDepartment.description}</p>
                    )}
                  </div>
                </div>
                <div className="header-actions">
                  <button className="btn-secondary" onClick={() => openEditModal(selectedDepartment)}>
                    <Edit2 size={16} />
                    Edit Department
                  </button>
                </div>
              </div>

              {/* Statistics Cards */}
              <div className="details-stats">
                <div className="stat-card">
                  <div className="stat-icon staff">
                    <Users size={24} />
                  </div>
                  <div className="stat-content">
                    <h3>{departmentStats?.total_staff || 0}</h3>
                    <p>Total Staff</p>
                    <span className="stat-detail">
                      {departmentStats?.active_staff || 0} active
                    </span>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon budget">
                    <DollarSign size={24} />
                  </div>
                  <div className="stat-content">
                    <h3>{formatCurrency(selectedDepartment.budget)}</h3>
                    <p>Department Budget</p>
                    <span className="stat-detail">
                      {formatCurrency(departmentStats?.total_salaries)} utilized
                    </span>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon performance">
                    <TrendingUp size={24} />
                  </div>
                  <div className="stat-content">
                    <h3>{calculateBudgetUtilization().toFixed(1)}%</h3>
                    <p>Budget Utilization</p>
                    <span className={`stat-detail ${calculateBudgetUtilization() > 90 ? 'warning' : ''}`}>
                      {calculateBudgetUtilization() > 90 ? 'High utilization' : 'Within budget'}
                    </span>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon attendance">
                    <BarChart3 size={24} />
                  </div>
                  <div className="stat-content">
                    <h3>{departmentStats?.avg_attendance || 0}%</h3>
                    <p>Avg. Attendance</p>
                    <span className="stat-detail">Last 30 days</span>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="details-section">
                <h3>Contact Information</h3>
                <div className="contact-grid">
                  {selectedDepartment.manager_name && (
                    <div className="contact-item">
                      <Users size={18} />
                      <div>
                        <span className="contact-label">Manager</span>
                        <span className="contact-value">{selectedDepartment.manager_name}</span>
                      </div>
                    </div>
                  )}
                  {selectedDepartment.contact_email && (
                    <div className="contact-item">
                      <Mail size={18} />
                      <div>
                        <span className="contact-label">Email</span>
                        <a href={`mailto:${selectedDepartment.contact_email}`} className="contact-value">
                          {selectedDepartment.contact_email}
                        </a>
                      </div>
                    </div>
                  )}
                  {selectedDepartment.contact_phone && (
                    <div className="contact-item">
                      <Phone size={18} />
                      <div>
                        <span className="contact-label">Phone</span>
                        <a href={`tel:${selectedDepartment.contact_phone}`} className="contact-value">
                          {selectedDepartment.contact_phone}
                        </a>
                      </div>
                    </div>
                  )}
                  {selectedDepartment.location && (
                    <div className="contact-item">
                      <MapPin size={18} />
                      <div>
                        <span className="contact-label">Location</span>
                        <span className="contact-value">{selectedDepartment.location}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Budget Breakdown */}
              {selectedDepartment.budget && (
                <div className="details-section">
                  <h3>Budget Breakdown</h3>
                  <div className="budget-breakdown">
                    <div className="budget-bar">
                      <div 
                        className="budget-fill"
                        style={{ width: `${Math.min(calculateBudgetUtilization(), 100)}%` }}
                      />
                    </div>
                    <div className="budget-details">
                      <div className="budget-item">
                        <span>Total Budget</span>
                        <span className="budget-value">{formatCurrency(selectedDepartment.budget)}</span>
                      </div>
                      <div className="budget-item">
                        <span>Staff Salaries</span>
                        <span className="budget-value">{formatCurrency(departmentStats?.total_salaries)}</span>
                      </div>
                      <div className="budget-item">
                        <span>Remaining</span>
                        <span className="budget-value remaining">
                          {formatCurrency((selectedDepartment.budget || 0) - (departmentStats?.total_salaries || 0))}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingDepartment ? 'Edit Department' : 'Create Department'}</h3>
              <button onClick={() => setShowModal(false)}>
                <XCircle size={20} />
              </button>
            </div>
            
            <form onSubmit={editingDepartment ? handleUpdateDepartment : handleCreateDepartment}>
              <div className="form-group">
                <label>Department Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Front Desk, Housekeeping"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the department"
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Budget</label>
                  <input
                    type="number"
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                    placeholder="Annual budget"
                    min="0"
                    step="1000"
                  />
                </div>
                <div className="form-group">
                  <label>Location</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g., Lobby, 2nd Floor"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Contact Email</label>
                  <input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="department@hotel.com"
                  />
                </div>
                <div className="form-group">
                  <label>Contact Phone</label>
                  <input
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : editingDepartment ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentTab;